import {shallowEqual, useDispatch, useSelector} from 'react-redux';

import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums';

import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {getConfigByApp, utilServerSideDeviceDetection} from '@utils';
import {metaInfo} from '@configs';
import {HOME, JOBFEED, PERSONALIZE_JOBFEED} from '@constants';

import type {GetServerSidePropsContext} from 'next/types';
import loadSEO from '@helpers/loadSEO';
import {SeoIntrup} from '@layouts/SeoLayout';
import {isCSR} from '@jobseeker/lotus/utils';
import PersonalizeTagSelection from '@components/PersonalizeJobfeed';
import PersonalizeTagSelectionV2 from '@components/PersonalizeJobfeedV2';
import {Box, Card, Grid} from '@mui/material';
import NavigationPanel from '@jobseeker/lotus/components/NavigationPanel';
import {useEffect, useState} from 'react';
import SelectedTags from '@components/SelectedTags';
import jobfeedService from '@jobseeker/common-core/services/jobfeed.service';
import userService from '@jobseeker/common-core/services/user.service';

import {useIsLargeDevice} from '@jobseeker/lotus/hooks/common';
import BackButtonHeader from '@jobseeker/lotus/components/BackButtonHeader';
import Dialog from '@jobseeker/lotus/components/Dialog';
import PersonalizeHeader from '@components/PersonalizeJobfeed/PersonalizeHeader';
import PersonalizeHeaderV2 from '@components/PersonalizeJobfeedV2/PersonalizeHeader';
import {useAlertCallback} from '@jobseeker/lotus/hooks/alert';
import InjectSearchOverlay from '@components/SearchInjection';

import type {AppState} from '@jobseeker/common-core/types';
import {openUrl, TrackEventUBA} from '@jobseeker/common-core/utils';
import {useRouter} from 'next/router';
import {categories} from '@components/PersonalizeJobfeedV2/constants';
import dynamic from 'next/dynamic';

const BottomSheet = dynamic(
  () => import('@jobseeker/lotus/elements/BottomSheet'),
  {ssr: false}
);

type PersonalizeJobfeedProps = {
  seoConfig: object;
  isMobile: boolean;
  skip: boolean;
};

type Tag = {id: number; name: string; count?: number; select?: boolean};

type CategoryTag = {
  categoryId: number;
  tags: Array<Tag>;
};

const PersonalizeJobfeed = ({
  seoConfig,
  isMobile,
  skip,
}: PersonalizeJobfeedProps) => {
  const metaData = getConfigByApp(metaInfo, JOBFEED, true, 'jobfeed');

  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  useValidateAuth(dispatch, true);

  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  const [selectedItem, setSelectedItem] = useState<string | number | undefined>(
    isAppIimjobs ? (isMobile ? 0 : 13) : 14
  );

  const [selectEnabled, setSelectEnabled] = useState(false);
  const [updateCalled, setUpdateCalled] = useState(false);
  const [openSelectedAlert, setOpenSelectedAlert] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const breadcrumbElements = [
    {
      name: 'Home',
      item: '/',
    },
    {
      name: 'Personalize Jobfeed',
      item: '',
    },
  ];

  const menuItems = isAppIimjobs
    ? [
        {label: 'Banking & Finance', key: 13},
        {label: 'Sales & Marketing', key: 14},
        {label: 'Consulting', key: 16},
        {label: 'HR & IR', key: 17},
        {label: 'IT & System', key: 15},
        {label: 'SCM & Operations', key: 19},
        {label: 'Legal', key: 21},
        {label: 'BPO', key: 22},
        {label: 'GenAI', key: 25},
      ]
    : categories.map(category => ({
        label: category.name,
        key: category.id,
      }));

  const [allTags, setAllTags] = useState<Array<CategoryTag>>([]);
  const [categoryTags, setCategoryTags] = useState<Array<Tag>>([]);
  const [userSelectedTags, setUserSelectedTags] = useState<Array<Tag>>([]);
  const [showNavPanel, setShowNavPanel] = useState(true);

  const {
    profile: {tags, id},
  } = useSelector((state: AppState) => state.user);

  const [MandateAlert, toggleMandateAlert] = useAlertCallback({
    primaryCallback: () => {},
    title: 'You need at least one tag selected!',
    subtitle:
      'This helps us show you relevant jobs. Please keep at least one tag selected.',
    primaryAction: 'Okay',
    useBottomSheet: true,
  });

  const getAllTags = async () => {
    const tagsWithCategory = await jobfeedService.getAllTagsWithCategory();
    setAllTags(tagsWithCategory);
    setTagsAgainstSelectedCategory(tagsWithCategory);
  };

  const mergeUnique = (arr1: Array<Tag>, arr2: Array<Tag>) => {
    const uniqueMap = new Map();

    const addItemsToMap = (arr: Array<Tag>) => {
      arr.forEach(item => {
        uniqueMap.set(item.id, item);
      });
    };

    addItemsToMap(arr1);
    addItemsToMap(arr2);

    return Array.from(uniqueMap.values());
  };

  const setTagsAgainstSelectedCategory = (
    catTags: Array<CategoryTag>,
    skipSearchCheck = false
  ) => {
    if (searchValue && !skipSearchCheck) {
      handleSearch(searchValue);
      return;
    }
    const userExistingSelection = new Set(tags?.map(tagInfo => tagInfo.tagId));
    const userCurrentSelected = new Set(
      userSelectedTags?.map(tagInfo => {
        if (tagInfo.select) {
          return tagInfo.id;
        }
      })
    );

    const userCurrentUnSelected = new Set(
      userSelectedTags?.map(tagInfo => {
        if (!tagInfo.select) {
          return tagInfo.id;
        }
      })
    );

    const selectedCategory = catTags.find(
      categoryInfo => categoryInfo.categoryId === selectedItem
    );

    const isSelected = (tagId: number) =>
      userExistingSelection.has(tagId) &&
      !userCurrentSelected.has(tagId) &&
      !userCurrentUnSelected.has(tagId)
        ? true
        : userExistingSelection.has(tagId) && userCurrentSelected.has(tagId)
        ? true
        : !userExistingSelection.has(tagId) && userCurrentSelected.has(tagId)
        ? true
        : userExistingSelection.has(tagId) && userCurrentUnSelected.has(tagId)
        ? false
        : false;

    if (selectedCategory?.tags.length) {
      const mappedTags = selectedCategory.tags?.map(tagInfo => {
        return {
          ...tagInfo,
          select: isSelected(tagInfo.id),
        };
      });
      setCategoryTags(mappedTags);
    }

    const userSelectedTagsInfo = catTags
      .flatMap(item => item.tags)
      .filter(tagInfo => userExistingSelection.has(tagInfo.id))
      .map(tagInfo => ({
        ...tagInfo,
        select: isSelected(tagInfo.id),
      }));

    const mergedTags = mergeUnique(userSelectedTags, userSelectedTagsInfo);
    setUserSelectedTags(mergedTags);
  };

  const updatedTheCurrentTagsState = (
    tagInfo: {
      name: string;
      id: number;
      select: boolean;
    },
    currentTags: Array<Tag>
  ) => {
    const elementIndex = currentTags.findIndex(tag => tag.id === tagInfo.id);
    if (elementIndex !== -1) {
      currentTags[elementIndex] = {
        ...currentTags[elementIndex],
        ...tagInfo,
      };

      return currentTags;
    }
  };

  const handleTagSelection = (
    name: string,
    tagId: number,
    operation: 'SELECT' | 'DESELECT'
  ) => {
    TrackEventUBA('tagSelectionClick', 'click', {
      page: 'Jobfeed',
      sessionUsername: sessionStorage.getItem('userId') || '',
    });

    if (operation === 'SELECT') {
      const isTagAlreadySelected = userSelectedTags?.some(
        tag => tag.id === tagId && tag.select
      );
      if (!isTagAlreadySelected) {
        const selectedElement = {
          id: tagId,
          name,
          select: true,
        };

        const updatedTags = updatedTheCurrentTagsState(
          selectedElement,
          categoryTags
        );

        updatedTags && setCategoryTags([...updatedTags]);

        setUserSelectedTags([selectedElement, ...userSelectedTags]);
      }
    } else {
      const deselectedTag = {
        id: tagId,
        name,
        select: false,
      };

      // check if any tag is remain selected in userSelectedTags
      // if userSelectedTags is single and that mapped with deselect id

      const operatedSelectedTags = userSelectedTags.filter(tag => tag.select);

      if (operatedSelectedTags.length === 1) {
        toggleMandateAlert();
        return;
      }

      const updated = updatedTheCurrentTagsState(deselectedTag, categoryTags);

      updated && setCategoryTags([...updated]);

      const deselected = updatedTheCurrentTagsState(
        deselectedTag,
        userSelectedTags
      );

      if (deselected?.length) {
        setUserSelectedTags([...deselected]);
      }
    }
    setSelectEnabled(true);
  };

  const resetTags = () => {
    setTagsAgainstSelectedCategory(allTags);
  };

  const handleSearch = (query: string) => {
    if (!query) {
      setSearchValue('');
      setTagsAgainstSelectedCategory(allTags, true);
      return;
    }

    setSearchValue(query);

    const lowerQuery = query.toLowerCase();

    const flattenTagArray = allTags.find(
      categoryInfo => categoryInfo.categoryId === selectedItem
    )?.tags;

    const selectedTagIds = new Set(tags?.map(tagInfo => tagInfo.tagId));

    const uniqueValues = new Set();
    const filteredTags = flattenTagArray
      ?.filter(tag => {
        const tagName = tag.name.toLowerCase();

        if (
          isAppIimjobs &&
          tagName.startsWith(lowerQuery) &&
          !uniqueValues.has(tagName)
        ) {
          uniqueValues.add(tagName);
          return true;
        }

        if (
          !isAppIimjobs &&
          tagName.includes(lowerQuery) &&
          !uniqueValues.has(tagName)
        ) {
          uniqueValues.add(tagName);
          return true;
        }

        return false;
      })
      .map(tag => ({
        ...tag,
        select: selectedTagIds.has(tag.id),
      }));

    filteredTags && setCategoryTags(filteredTags);
  };

  const handleSubmitConfirm = async () => {
    if (isAppIimjobs && isMobile && !!selectedItem) {
      setSelectedItem(0);
      setShowNavPanel(true);
      return;
    }

    setUpdateCalled(true);

    const userSelection = new Set(tags?.map(tagInfo => tagInfo.tagId));

    const tagsWithKeyValue = userSelectedTags.reduce(
      (acc, tag) => {
        if (
          (userSelection.has(tag.id) && !tag.select) ||
          (!userSelection.has(tag.id) && tag.select)
        ) {
          acc[tag.id] = Number(tag.select);
        }
        return acc;
      },
      {} as {[key: number]: number}
    );

    if (!isAppIimjobs) {
      if (Object.keys(tagsWithKeyValue).length > 0) {
        await userService.updatePersonalizedTags(tagsWithKeyValue);
      }
      openUrl('/jobfeed', '_self');
      return;
    } else {
      if (Object.keys(tagsWithKeyValue).length > 0) {
        await userService.updatePersonalizedTags(tagsWithKeyValue);
        openUrl('/jobfeed', '_self');
        return;
      }
    }
    setUpdateCalled(false);
  };

  const getUserSelectedTagCount = () => {
    const userExistingSelection = new Set(tags?.map(tagInfo => tagInfo.tagId));
    const userCurrentSelected = new Set(
      userSelectedTags?.map(tagInfo => {
        if (tagInfo.select) {
          return tagInfo.id;
        }
      })
    );

    const userCurrentUnSelected = new Set(
      userSelectedTags?.map(tagInfo => {
        if (!tagInfo.select) {
          return tagInfo.id;
        }
      })
    );

    const selectedCategory = allTags.find(
      categoryInfo => categoryInfo.categoryId === selectedItem
    );

    const isSelected = (tagId: number) =>
      userExistingSelection.has(tagId) &&
      !userCurrentSelected.has(tagId) &&
      !userCurrentUnSelected.has(tagId)
        ? true
        : userExistingSelection.has(tagId) && userCurrentSelected.has(tagId)
        ? true
        : !userExistingSelection.has(tagId) && userCurrentSelected.has(tagId)
        ? true
        : userExistingSelection.has(tagId) && userCurrentUnSelected.has(tagId)
        ? false
        : false;

    if (selectedCategory?.tags.length) {
      const mappedTags = selectedCategory.tags?.map(tagInfo => {
        return {
          ...tagInfo,
          select: isSelected(tagInfo.id),
        };
      });
      return mappedTags.filter(tag => tag.select)?.length ?? 0;
    }
  };

  const isAboveMD = useIsLargeDevice('md');
  const WrapperComponent = isAboveMD ? Dialog : BottomSheet;

  useEffect(() => {
    if (id) {
      getAllTags();
    }
  }, [id]);

  useEffect(() => {
    if (!!selectedItem && isMobile) {
      setShowNavPanel(false);
      resetTags();
    }

    if (!isMobile) {
      resetTags();
    }
  }, [selectedItem]);

  const isSearchEnabled = useSelector(
    (state: AppState) => state.app.layout.searchOverlay,
    shallowEqual
  );

  const router = useRouter();

  useEffect(() => {
    TrackEventUBA(
      router?.query?.fl == 'rg'
        ? 'registrationTagsSelectionView'
        : 'tagselectionPageView',
      'view',
      {
        ref: router.query.ref || '',
        profileType: router.query.profiletype || '',
        source: router.query.source || '',
        status: router.query.status || '',
        pageName:
          router?.query?.fl == 'rg'
            ? 'Registration Page TagSelection Information'
            : 'Tag Selection Page',
        regStep: 'jobfeedtag',
        sessionUsername: `${sessionStorage.getItem('userId')}`,
      }
    );
  }, [router.query]);
  const [personalizedPopUpOpen, setPersonalizedPopUpOpen] =
    useState<boolean>(false);
  const personalizedJobfeedOnMobile = () => {
    setPersonalizedPopUpOpen(true);
  };
  return (
    <>
      <Interceptor metaInfo={metaData}>
        <SeoLayout seoConfig={seoConfig} paginationEnabled={true} />
        <NavLayout
          HeaderComp={
            isAppIimjobs && isMobile && selectedItem
              ? () => (
                  <Box
                    position={'absolute'}
                    sx={{
                      width: '100%',
                    }}
                  >
                    <BackButtonHeader
                      title={
                        menuItems.find(item => item.key === selectedItem)
                          ?.label ?? ''
                      }
                      onBackClick={() => {
                        setSelectedItem(0);
                        setShowNavPanel(true);
                      }}
                    />
                  </Box>
                )
              : undefined
          }
        >
          <ResponsiveLayout
            variant={VersionVariants.VERSION_TWO}
            breadcrumbElements={isAppIimjobs ? breadcrumbElements : []}
          >
            {isCSR() && (
              <SeoIntrup
                pageNo={0}
                nextPage={false}
                type={PERSONALIZE_JOBFEED}
              />
            )}
            {isAppIimjobs ? (
              <Box display={'flex'} justifyContent={'flex-start'}>
                <InjectSearchOverlay isSearchEnabled={isSearchEnabled} />
                <Grid container>
                  {showNavPanel && (
                    <Grid
                      item
                      xs
                      maxWidth={{md: 'fit-content'}}
                      sx={{
                        position: 'relative',
                      }}
                    >
                      <NavigationPanel
                        title={
                          isMobile ? (
                            <Box
                              sx={{
                                ml: 1,
                                mb: 1.5,
                              }}
                            >
                              <PersonalizeHeader
                                isMobile={isMobile}
                                searchValue={searchValue}
                              />
                            </Box>
                          ) : (
                            'Job Categories'
                          )
                        }
                        menu={menuItems}
                        selectedItem={selectedItem}
                        onSelect={item => {
                          setSelectedItem(item);
                        }}
                      />
                    </Grid>
                  )}
                  <Grid
                    item
                    md
                    sx={{
                      maxWidth: {md: 806, xs: '100%'},
                      width: '100%',
                    }}
                  >
                    <PersonalizeTagSelection
                      currentSelectedTags={categoryTags}
                      handleTagSelection={handleTagSelection}
                      handleSearch={handleSearch}
                      handleSubmitConfirm={handleSubmitConfirm}
                      selectEnabled={selectEnabled}
                      updateCalled={updateCalled}
                      resetTags={resetTags}
                      selectedCount={
                        userSelectedTags.filter(tag => tag.select)?.length ?? 0
                      }
                      toggleSelectedTagsAlert={() => {
                        setOpenSelectedAlert(true);
                      }}
                      categorySelected={!!selectedItem}
                      isMobile={isMobile}
                      skip={skip}
                      searchValue={searchValue}
                    />
                  </Grid>
                  <Grid
                    item
                    md={3}
                    sx={{
                      maxWidth: '344px',
                      position: 'relative',
                      height: '600px',
                      display: {xxs: 'none', lg: 'block'},
                    }}
                  >
                    <Card
                      sx={{
                        boxShadow: 2,
                        borderRadius: 4,
                        marginLeft: {md: 2},
                        py: 3,
                        height: 'inherit',
                        overflow: 'scroll',
                      }}
                    >
                      <SelectedTags
                        selected={userSelectedTags}
                        handleTagSelection={handleTagSelection}
                      />
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Box
                sx={{
                  overflow: {xs: 'scroll', md: 'hidden'},
                  position: 'relative',
                  height: '90vh',
                }}
              >
                <PersonalizeHeaderV2
                  isMobile={!isAboveMD}
                  searchValue={searchValue}
                  onSelect={item => {
                    setSelectedItem(item);
                  }}
                  openPopup={personalizedJobfeedOnMobile}
                  selectedItem={selectedItem}
                />
                <PersonalizeTagSelectionV2
                  currentSelectedTags={categoryTags}
                  handleTagSelection={handleTagSelection}
                  handleSearch={handleSearch}
                  handleSubmitConfirm={handleSubmitConfirm}
                  selectEnabled={selectEnabled}
                  updateCalled={updateCalled}
                  resetTags={resetTags}
                  selectedCount={
                    userSelectedTags.filter(tag => tag.select)?.length ?? 0
                  }
                  toggleSelectedTagsAlert={() => {
                    setOpenSelectedAlert(true);
                  }}
                  categorySelected={!!selectedItem}
                  isMobile={!isAboveMD}
                  skip={skip}
                  searchValue={searchValue}
                  selectedItem={selectedItem}
                  selectedCategoryTagsCount={getUserSelectedTagCount() ?? 0}
                  personalizedPopUpOpen={personalizedPopUpOpen}
                  setPersonalizedPopUpOpen={setPersonalizedPopUpOpen}
                />
              </Box>
            )}
            <WrapperComponent
              open={openSelectedAlert}
              maxWidth={'lg'}
              title={`Selected Tags (${
                userSelectedTags.filter(tag => tag.select)?.length ?? 0
              })`}
              onClose={() => {
                setOpenSelectedAlert(false);
              }}
              variant={VersionVariants.VERSION_THREE}
              bannerMode={false}
              defaultSnap={({lastSnap, snapPoints}: any) =>
                lastSnap ?? Math.max(...snapPoints)
              }
              snapPoints={({maxHeight}: {maxHeight: number}) => [
                maxHeight - 50,
              ]}
              onDismiss={() => {
                setOpenSelectedAlert(false);
              }}
              sx={{
                mx: 11.8,
              }}
            >
              <SelectedTags
                selected={userSelectedTags}
                handleTagSelection={handleTagSelection}
              />
            </WrapperComponent>
            <MandateAlert />
          </ResponsiveLayout>
        </NavLayout>
      </Interceptor>
    </>
  );
};

export const getServerSideProps = wrapper.getStaticProps(
  store => async (context: GetServerSidePropsContext) => {
    const flow = context.query?.fl || '';

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: PERSONALIZE_JOBFEED},
    });

    const {isMobile} = utilServerSideDeviceDetection(context);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    const seoConfig = await loadSEO(HOME);

    return {
      props: {seoConfig, isMobile, skip: flow === 'rg'},
    };
  }
);

export default PersonalizeJobfeed;
